// src/views/LobbyView.jsx
import React, { useState } from 'react';
import {
  Book,
  GraduationCap,
  Sparkles,
  Box,
  Lock,
  ChevronRight,
  ClipboardCheck,
  X,
  Zap
} from 'lucide-react';
import Mascot from '../components/Mascot';
import PurchaseSuccessModal from '../components/modals/PurchaseSuccessModal';
import { useLobbyLogic } from '../hooks/useLobbyLogic';
import { WEEKLY_CHALLENGE } from '../data/weeklyChallenge';
import { WEEKLY_QUIZ } from '../data/weeklyQuiz';

const SECTIONS = [
  { id: 'modules_base',  title: 'Ruta Principal', subtitle: 'Módulos y Unidades',    icon: <Book size={32} />,         view: 'modules',    color: 'bg-indigo-500', cost: 0 },
  { id: 'grammar_base',  title: 'Gramática',       subtitle: 'Biblioteca de consulta',icon: <GraduationCap size={32} />, view: 'grammar',    color: 'bg-blue-500',   cost: 0 },
  { id: 'vocabulary_hub',title: 'Vocabulario',     subtitle: 'Palabras y frases',     icon: <Sparkles size={32} />,     view: 'vocabulary', color: 'bg-emerald-500',cost: 0 },
  { id: 'surprise_chest',title: 'Zona Misteriosa', subtitle: 'Sets sorpresa',         icon: <Box size={32} />,          view: 'mysterious', color: 'bg-amber-500',  cost: 100 },
];

// Grammar Quest — sección separada con acceso maestro o 500 XP
const GRAMMAR_QUEST_SECTION = {
  id: 'grammar_quest',
  title: 'Grammar Word Quest',
  subtitle: 'Mini reto gramatical A2',
  view: 'grammar-quest',
  cost: 500,
};

const WEEKLY_DISMISSED_KEY = 'educat_dismissed_weekly_quiz';

const activeWeekly = WEEKLY_CHALLENGE || WEEKLY_QUIZ;

const wasWeeklyDismissed = () => {
  try {
    return localStorage.getItem(WEEKLY_DISMISSED_KEY) === activeWeekly?.id;
  } catch {
    return false;
  }
};

export default function LobbyView({
  setView,
  userProgress,
  setActiveModal,
  setItemToBuy,
  onStartWeeklyQuiz, // opcional: se conectará al modal o al inicio del set
  isTeacherMode,
}) {
  const { isJumping, triggerJump, checkAccess } = useLobbyLogic(userProgress);
  const activeSkin = userProgress.currentSkin || 'bribark';

  const [showWeeklyBubble, setShowWeeklyBubble] = useState(
    () => activeWeekly?.isActive && !wasWeeklyDismissed()
  );

  const dismissWeeklyBubble = () => {
    try {
      if (activeWeekly?.id) localStorage.setItem(WEEKLY_DISMISSED_KEY, activeWeekly.id);
    } catch {
      // Si localStorage no está disponible, solo se oculta en esta sesión
    }
    setShowWeeklyBubble(false);
  };

const handleOpenWeeklyQuiz = () => {
  if (onStartWeeklyQuiz) {
    onStartWeeklyQuiz(activeWeekly);
  }
};

  // ✅ Lógica original de compra — sin cambios
  const handleLockedSection = (section) => {
    setActiveModal({
      type: 'CONFIRM_PURCHASE',
      data: {
        id: section.id,
        title: section.title,
        subtitle: section.subtitle,
        cost: section.cost,
        icon: '🔮',
        description: 'Desbloquea sets de vocabulario exclusivos y skins secretas.'
      }
    });
  };

  // ✅ Acceso al Grammar Quest — modo maestro O 500 XP
  const hasGrammarQuestAccess = isTeacherMode ||
    (userProgress?.unlockedContent || []).includes(GRAMMAR_QUEST_SECTION.id);

  const handleGrammarQuestEntry = () => {
    if (hasGrammarQuestAccess) {
      setView(GRAMMAR_QUEST_SECTION.view);
    } else {
      setActiveModal({
        type: 'CONFIRM_PURCHASE',
        data: {
          id: GRAMMAR_QUEST_SECTION.id,
          title: GRAMMAR_QUEST_SECTION.title,
          subtitle: GRAMMAR_QUEST_SECTION.subtitle,
          cost: GRAMMAR_QUEST_SECTION.cost,
          icon: '🎯',
          description: 'Desbloquea el mini-reto gramatical interactivo con rueda de palabras, colección y reto final.'
        }
      });
    }
  };

  return (
    <div className="space-y-6 pb-10 animate-in fade-in duration-700">

      {/* MASCOTA */}
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <div
            className="hover:scale-110 transition-transform cursor-pointer"
            onClick={() => {
              triggerJump();
              setTimeout(() => setActiveModal({ type: 'XP_STORE' }), 300);
            }}
          >
            <Mascot type={activeSkin} isJumping={isJumping} />
          </div>
        </div>
        <div>
          <h1 className="text-3xl font-black text-slate-800">¿Qué aprenderemos hoy?</h1>
          <p className="text-slate-500 font-bold tracking-tight">Elige tu próximo destino</p>
        </div>

        {/* DESAFÍO SEMANAL */}
        {activeWeekly?.isActive && showWeeklyBubble && (
          <div className="relative mx-auto mt-2 max-w-md rounded-[1.75rem] border-2 border-indigo-100 bg-gradient-to-br from-white to-indigo-50 p-4 text-left shadow-lg animate-in fade-in slide-in-from-top-2 duration-500">
            <button
              onClick={dismissWeeklyBubble}
              aria-label="Ocultar desafío semanal"
              className="absolute right-3 top-3 rounded-full p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            >
              <X size={16} />
            </button>

            <div className="flex items-center gap-3 pr-6">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-indigo-600 text-white shadow-md">
                <ClipboardCheck size={24} />
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-black uppercase tracking-widest text-indigo-500">
                  Desafío semanal
                </p>
                <p className="truncate text-sm font-black text-slate-800">
                  {activeWeekly.title}
                </p>
                <p className="mt-0.5 text-[11px] font-medium text-slate-500">
                  {activeWeekly.sections ? `${activeWeekly.sections.length} mini retos` : `${activeWeekly.totalQuestions} preguntas`} · ~{activeWeekly.estimatedMinutes} min
                </p>
              </div>
            </div>

            <button
              onClick={handleOpenWeeklyQuiz}
              className="mt-3 w-full rounded-2xl border-b-4 border-indigo-800 bg-indigo-600 py-3 text-sm font-black text-white transition hover:bg-indigo-700 active:translate-y-0.5 active:border-b-0"
            >
              Entrar al desafío
            </button>
          </div>
        )}
      </div>

      {/* CHIPS HORIZONTALES — primeras 3 secciones */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide px-1">
        {SECTIONS.slice(0, 3).map((section) => {
          const hasAccess = checkAccess(section);
          const emojisChip = { modules_base: '🗺️', grammar_base: '📖', vocabulary_hub: '✨' };
          return (
            <button
              key={section.id}
              onClick={() => hasAccess ? setView(section.view) : handleLockedSection(section)}
              className={`shrink-0 flex items-center gap-2 px-4 py-3 rounded-2xl border-2 transition-all active:scale-95 font-black text-sm
                ${hasAccess
                  ? `${section.color} text-white border-transparent shadow-lg`
                  : 'bg-slate-100 text-slate-400 border-slate-200'
                }`}
            >
              <span className="text-lg">{emojisChip[section.id]}</span>
              <span className="text-[11px]">{section.title}</span>
            </button>
          );
        })}
      </div>

      {/* TARJETA FEATURED — Ruta Principal */}
      {(() => {
        const main = SECTIONS[0];
        const hasAccess = checkAccess(main);
        const completedSets = userProgress?.completedSets?.length || 0;
        const totalEstimated = 80;
        const progressPct = Math.min((completedSets / totalEstimated) * 100, 100);

        return (
          <div className="rounded-[2.5rem] overflow-hidden shadow-2xl border-b-8 border-indigo-800 relative bg-gradient-to-br from-indigo-500 via-indigo-600 to-purple-700">
            <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-purple-400/20 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4 pointer-events-none" />

            <div className="relative z-10 p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-indigo-200 text-[10px] font-black uppercase tracking-widest">Ruta Principal</p>
                  <h3 className="text-white font-black text-xl leading-tight">Tu camino al inglés</h3>
                </div>
                <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center text-3xl shadow-inner">
                  🗺️
                </div>
              </div>

              <div className="flex gap-3 text-[11px] font-bold text-indigo-200">
                <span>📚 {completedSets} sets completados</span>
                <span>·</span>
                <span>⚡ +500 XP disponibles</span>
              </div>

              <div className="space-y-1.5">
                <div className="flex justify-between text-[10px] font-black text-indigo-200">
                  <span>Tu progreso general</span>
                  <span>{Math.round(progressPct)}%</span>
                </div>
                <div className="h-3 bg-white/20 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-cyan-300 to-white rounded-full transition-all duration-700"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>

              <button
                onClick={() => setView(main.view)}
                className="w-full py-4 rounded-2xl font-black text-indigo-700 text-sm bg-white shadow-xl hover:bg-indigo-50 active:scale-95 transition-all border-b-4 border-indigo-200"
              >
                🚀 Continuar Aprendiendo
              </button>
            </div>
          </div>
        );
      })()}

      {/* GRID 2 COLUMNAS — Vocabulario + Zona Misteriosa */}
      <div className="grid grid-cols-2 gap-3">
        {SECTIONS.slice(2).map((section) => {
          const hasAccess = checkAccess(section);
          const emojis = { vocabulary_hub: '✨', surprise_chest: '🔮' };

          return (
            <button
              key={section.id}
              onClick={() => hasAccess ? setView(section.view) : handleLockedSection(section)}
              className={`p-4 rounded-[2rem] border-2 border-b-4 flex flex-col items-center gap-2 text-center transition-all active:scale-95 active:border-b-0
                ${hasAccess
                  ? 'bg-white border-slate-100 shadow-md hover:border-indigo-200 hover:shadow-lg'
                  : 'bg-slate-50 border-slate-200 border-dashed'
                }`}
            >
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl shadow-md relative overflow-hidden
                ${hasAccess ? section.color : 'bg-slate-200'}`}>
                <div className="absolute top-0 left-0 w-full h-1/2 bg-white/30 rounded-t-2xl pointer-events-none" />
                <span className="relative z-10">{hasAccess ? emojis[section.id] : '🔒'}</span>
              </div>
              <div>
                <p className={`font-black text-sm leading-tight ${hasAccess ? 'text-slate-800' : 'text-slate-400'}`}>
                  {section.title}
                </p>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mt-0.5">
                  {section.subtitle}
                </p>
              </div>
              {!hasAccess && (
                <span className="text-[9px] font-black bg-amber-100 text-amber-600 px-2 py-0.5 rounded-full border border-amber-200">
                  🔑 {section.cost} XP
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── GRAMMAR WORD QUEST — Tarjeta destacada ── */}
      <button
        onClick={handleGrammarQuestEntry}
        className="w-full rounded-[2rem] overflow-hidden border-2 border-b-4 transition-all active:scale-[0.98] active:border-b-0 text-left"
        style={hasGrammarQuestAccess
          ? { borderColor: 'rgba(109,40,217,0.35)', borderBottomColor: 'rgba(88,28,135,0.5)', background: 'linear-gradient(135deg, #2e1065 0%, #4c1d95 40%, #5b21b6 100%)' }
          : { borderColor: '#e2e8f0', borderBottomColor: '#cbd5e1', background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }
        }
      >
        <div className="relative p-5 overflow-hidden">
          {/* Glow de fondo decorativo */}
          {hasGrammarQuestAccess && (
            <>
              <div className="absolute top-0 right-0 w-40 h-40 rounded-full blur-3xl pointer-events-none" style={{ background: 'rgba(167,139,250,0.2)', transform: 'translate(30%, -30%)' }} />
              <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full blur-2xl pointer-events-none" style={{ background: 'rgba(236,72,153,0.1)', transform: 'translate(-20%, 30%)' }} />
            </>
          )}

          <div className="relative z-10 flex items-start gap-4">
            {/* Ícono */}
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl shadow-lg shrink-0 relative overflow-hidden"
              style={hasGrammarQuestAccess
                ? { background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)', boxShadow: '0 4px 20px rgba(109,40,217,0.4)' }
                : { background: '#e2e8f0' }
              }
            >
              <div className="absolute top-0 left-0 w-full h-1/2 bg-white/25 rounded-t-2xl pointer-events-none" />
              <span className="relative z-10">{hasGrammarQuestAccess ? '🎯' : '🔒'}</span>
            </div>

            {/* Texto */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <p
                  className="font-black text-sm leading-tight"
                  style={{ color: hasGrammarQuestAccess ? 'white' : '#475569' }}
                >
                  {GRAMMAR_QUEST_SECTION.title}
                </p>
                <span
                  className="text-[9px] font-black px-2 py-0.5 rounded-full shrink-0"
                  style={hasGrammarQuestAccess
                    ? { background: 'rgba(167,139,250,0.3)', color: '#c4b5fd', border: '1px solid rgba(167,139,250,0.3)' }
                    : { background: '#f1f5f9', color: '#94a3b8', border: '1px solid #e2e8f0' }
                  }
                >
                  A2
                </span>
              </div>
              <p
                className="text-[11px] font-medium"
                style={{ color: hasGrammarQuestAccess ? 'rgba(196,181,253,0.8)' : '#94a3b8' }}
              >
                {GRAMMAR_QUEST_SECTION.subtitle}
              </p>
              <div className="flex items-center gap-3 mt-2">
                <span
                  className="text-[10px] font-bold"
                  style={{ color: hasGrammarQuestAccess ? 'rgba(196,181,253,0.7)' : '#94a3b8' }}
                >
                  🎯 Rueda 3D · 📖 Colección · 🏆 Reto Final
                </span>
              </div>
            </div>

            {/* Acceso / Costo */}
            <div className="shrink-0 flex flex-col items-end gap-1.5 self-center">
              {hasGrammarQuestAccess ? (
                <div
                  className="flex items-center gap-1 px-3 py-1.5 rounded-xl font-black text-xs"
                  style={{ background: 'rgba(167,139,250,0.2)', color: '#c4b5fd', border: '1px solid rgba(167,139,250,0.3)' }}
                >
                  Entrar →
                </div>
              ) : (
                <div className="flex items-center gap-1 bg-amber-100 text-amber-600 px-3 py-1.5 rounded-xl font-black text-xs border border-amber-200">
                  <Zap size={11} />
                  {GRAMMAR_QUEST_SECTION.cost} XP
                </div>
              )}
            </div>
          </div>
        </div>
      </button>

      {/* BANNERS CEFR — sin cambios */}
      <div className="bg-indigo-50/50 border-2 border-indigo-100 border-dashed p-4 rounded-3xl text-center">
        <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-3">
          ⚡ Gana XP para descubrir secretos
        </p>

        {!userProgress?.cefrLevel && (
          <button
            onClick={() => setView('placement-select')}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-3xl p-4 flex items-center gap-4 shadow-xl shadow-indigo-200 hover:scale-[1.01] active:scale-[0.99] transition-all border-b-4 border-indigo-800 mb-2"
          >
            <div className="text-3xl">🎯</div>
            <div className="flex-1 text-left">
              <p className="text-[10px] font-black uppercase tracking-widest text-indigo-200">Nuevo · Gratis</p>
              <p className="text-sm font-black">¿Cuál es tu nivel de inglés?</p>
              <p className="text-xs text-indigo-200">Test rápido A1–C2 · 5 minutos</p>
            </div>
            <div className="text-2xl">→</div>
          </button>
        )}

        {userProgress?.cefrLevel && (
          <button
            onClick={() => setView('placement-select')}
            className="w-full bg-white rounded-2xl p-3 flex items-center gap-3 shadow-sm border-2 border-indigo-100 hover:border-indigo-300 hover:shadow-md transition-all mb-2"
          >
            <div className="w-10 h-10 rounded-2xl bg-indigo-100 flex items-center justify-center">
              <span className="text-lg font-black text-indigo-600">{userProgress.cefrLevel}</span>
            </div>
            <div className="flex-1 text-left">
              <p className="text-xs font-black text-indigo-600">Tu nivel CEFR</p>
              <p className="text-[10px] text-slate-400">{userProgress.cefrTestDate} · Toca para repetir el test</p>
            </div>
            <span className="text-slate-300 text-sm">→</span>
          </button>
        )}
      </div>

    </div>
  );
}